import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  TrendingUp, 
  Plus, 
  Pencil, 
  Trash2, 
  Check, 
  X,
  AlertTriangle,
  RefreshCw,
  Calendar,
  Euro,
  Wallet,
  BarChart3,
  Info
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS = [
  { value: 1, label: 'Janeiro' },
  { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' },
  { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' },
  { value: 12, label: 'Dezembro' }
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value || 0);
};

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

const initialFormData = {
  month: new Date().getMonth() + 1,
  year: currentYear,
  profit_amount: '',
  initial_capital: '',
  notes: ''
};

export default function FundGrowthManager() {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deletingEntry, setDeletingEntry] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/fund-growth`);
      setEntries(response.data.entries || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingEntry(null);
    setFormData(initialFormData);
    setShowModal(true);
  };

  const handleOpenEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      month: entry.month,
      year: entry.year,
      profit_amount: entry.profit_amount?.toString() || '',
      initial_capital: entry.initial_capital?.toString() || '',
      notes: entry.notes || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.profit_amount) {
      toast.error('Por favor indique o lucro do mês');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        month: parseInt(formData.month),
        year: parseInt(formData.year),
        profit_amount: parseFloat(formData.profit_amount),
        initial_capital: formData.initial_capital ? parseFloat(formData.initial_capital) : null,
        notes: formData.notes || null
      };

      if (editingEntry) {
        await axios.put(`${API_URL}/admin/fund-growth/${editingEntry.id}`, payload);
        toast.success('Entrada atualizada com sucesso');
      } else {
        await axios.post(`${API_URL}/admin/fund-growth`, payload);
        toast.success('Entrada criada com sucesso');
      }

      setShowModal(false);
      fetchEntries();
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error(error.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingEntry) return;

    try {
      await axios.delete(`${API_URL}/admin/fund-growth/${deletingEntry.id}`);
      toast.success('Entrada eliminada com sucesso');
      setShowDeleteModal(false);
      setDeletingEntry(null);
      fetchEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Erro ao eliminar');
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fund-growth-manager-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <TrendingUp className="w-7 h-7 text-emerald-400" />
            Gestão do Crescimento do Fundo
          </h1>
          <p className="text-slate-400 mt-1">
            Registe os lucros mensais do fundo para mostrar aos investidores
          </p>
        </div>
        <Button
          onClick={handleOpenCreate}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="add-entry-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Adicionar Mês
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 rounded-xl p-4 border border-emerald-500/20">
          <div className="flex items-center gap-2 text-emerald-400 mb-2">
            <Wallet className="w-5 h-5" />
            <span className="text-xs font-medium uppercase">Capital Atual</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatCurrency(summary.current_capital)}</p>
        </div>
        
        <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl p-4 border border-blue-500/20">
          <div className="flex items-center gap-2 text-blue-400 mb-2">
            <TrendingUp className="w-5 h-5" />
            <span className="text-xs font-medium uppercase">Lucro Total</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatCurrency(summary.total_profit)}</p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-xl p-4 border border-purple-500/20">
          <div className="flex items-center gap-2 text-purple-400 mb-2">
            <BarChart3 className="w-5 h-5" />
            <span className="text-xs font-medium uppercase">Média Mensal</span>
          </div>
          <p className="text-2xl font-bold text-white">{formatCurrency(summary.average_monthly_profit)}</p>
        </div>
        
        <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 rounded-xl p-4 border border-amber-500/20">
          <div className="flex items-center gap-2 text-amber-400 mb-2">
            <Calendar className="w-5 h-5" />
            <span className="text-xs font-medium uppercase">Meses Registados</span>
          </div>
          <p className="text-2xl font-bold text-white">{summary.total_entries || 0}</p>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-slate-300">
            <p className="font-medium text-blue-400 mb-1">Como funciona:</p>
            <ul className="list-disc list-inside space-y-1 text-slate-400">
              <li><strong>Lucro do Mês:</strong> O lucro/crescimento obtido nesse mês</li>
              <li><strong>Capital Inicial:</strong> (Opcional) Se definir, será usado como base para o cálculo acumulado a partir desse mês</li>
              <li>O sistema soma automaticamente todos os lucros e calcula a média de crescimento</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Entries List */}
      {entries.length === 0 ? (
        <div className="text-center py-16 bg-white/5 rounded-2xl border border-white/10">
          <TrendingUp className="w-12 h-12 text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">Sem registos</h3>
          <p className="text-slate-400 mb-4">Comece por adicionar o primeiro mês de lucros</p>
          <Button onClick={handleOpenCreate} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            Adicionar Primeiro Mês
          </Button>
        </div>
      ) : (
        <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-xs text-slate-400 font-medium uppercase tracking-wider px-4 py-3">Mês</th>
                  <th className="text-right text-xs text-slate-400 font-medium uppercase tracking-wider px-4 py-3">Lucro</th>
                  <th className="text-right text-xs text-slate-400 font-medium uppercase tracking-wider px-4 py-3 hidden sm:table-cell">Capital Inicial</th>
                  <th className="text-left text-xs text-slate-400 font-medium uppercase tracking-wider px-4 py-3 hidden md:table-cell">Notas</th>
                  <th className="text-right text-xs text-slate-400 font-medium uppercase tracking-wider px-4 py-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, index) => (
                  <tr 
                    key={entry.id} 
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    data-testid={`entry-row-${index}`}
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-500" />
                        <span className="text-white font-medium">{entry.month_name} {entry.year}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className={`font-semibold ${entry.profit_amount >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {entry.profit_amount >= 0 ? '+' : ''}{formatCurrency(entry.profit_amount)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right hidden sm:table-cell">
                      {entry.initial_capital ? (
                        <span className="text-blue-400">{formatCurrency(entry.initial_capital)}</span>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      {entry.notes ? (
                        <span className="text-slate-400 text-sm truncate max-w-[200px] block">{entry.notes}</span>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          onClick={() => handleOpenEdit(entry)}
                          variant="ghost"
                          size="sm"
                          className="text-slate-400 hover:text-blue-400"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => {
                            setDeletingEntry(entry);
                            setShowDeleteModal(true);
                          }}
                          variant="ghost"
                          size="sm"
                          className="text-slate-400 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#1c262d] border-white/10 max-w-md" data-testid="entry-form-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              {editingEntry ? 'Editar Registo' : 'Novo Registo Mensal'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Month & Year */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Mês *</Label>
                <Select
                  value={formData.month.toString()}
                  onValueChange={(value) => setFormData({ ...formData, month: parseInt(value) })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1c262d] border-white/10">
                    {MONTHS.map((m) => (
                      <SelectItem key={m.value} value={m.value.toString()} className="text-white hover:bg-white/10">
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Ano *</Label>
                <Select
                  value={formData.year.toString()}
                  onValueChange={(value) => setFormData({ ...formData, year: parseInt(value) })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1c262d] border-white/10">
                    {YEARS.map((y) => (
                      <SelectItem key={y} value={y.toString()} className="text-white hover:bg-white/10">
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Profit Amount */}
            <div>
              <Label className="text-slate-300">Lucro do Mês (€) *</Label>
              <div className="relative mt-1">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="number"
                  value={formData.profit_amount}
                  onChange={(e) => setFormData({ ...formData, profit_amount: e.target.value })}
                  placeholder="Ex: 15000"
                  className="bg-white/5 border-white/10 text-white pl-10"
                  step="0.01"
                  data-testid="profit-amount-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Lucro/crescimento obtido neste mês</p>
            </div>

            {/* Initial Capital (Optional) */}
            <div>
              <Label className="text-slate-300">Capital Inicial (opcional)</Label>
              <div className="relative mt-1">
                <Wallet className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="number"
                  value={formData.initial_capital}
                  onChange={(e) => setFormData({ ...formData, initial_capital: e.target.value })}
                  placeholder="Ex: 500000"
                  className="bg-white/5 border-white/10 text-white pl-10"
                  step="0.01"
                  data-testid="initial-capital-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Se definido, será a base para o cálculo acumulado</p>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-slate-300">Notas (opcional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Observações sobre este mês..."
                className="bg-white/5 border-white/10 text-white mt-1 resize-none"
                rows={2}
              />
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="border-white/10 text-slate-300 hover:bg-white/5"
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="save-entry-btn"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    A guardar...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    {editingEntry ? 'Atualizar' : 'Criar'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="bg-[#1c262d] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Eliminar Registo
            </DialogTitle>
          </DialogHeader>

          <p className="text-slate-300">
            Tem a certeza que deseja eliminar o registo de <strong>{deletingEntry?.month_name} {deletingEntry?.year}</strong>?
          </p>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              className="border-white/10 text-slate-300 hover:bg-white/5"
            >
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
